import React, { useState } from 'react';
import { 
  Work as WorkIcon,
  AutoFixHigh as AutoFixHighIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Grid,
  InputAdornment
} from '@mui/material';
import { generateExperiences } from '../services/api';

const GenerateExperience: React.FC = () => {
  const [jobRole, setJobRole] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const [experiences, setExperiences] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleGenerate = async () => {
    if (!jobRole.trim()) {
      setError('Please enter a job role');
      return;
    }

    setLoading(true);
    setError(null);
    setExperiences([]);

    try {
      const generatedExperiences = await generateExperiences(
        jobRole,
        jobUrl.trim() || undefined
      );
      setExperiences(generatedExperiences);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate experiences';
      
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (errorMessage.includes('Invalid request')) {
        const actualError = errorMessage.replace('Invalid request: ', '');
        setError(actualError);
      } else if (jobUrl && errorMessage.includes('Could not extract job description')) {
        setError('Unable to access the job posting. Please check if the URL is correct and publicly accessible.');
      } else if (errorMessage.includes('500')) {
        setError('Server error: Our system is temporarily unavailable. Please try again in a few minutes.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Card elevation={0} sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 3
      }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <WorkIcon color="primary" />
            <Typography variant="h5" fontWeight="600">
              Generate Job Experiences
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Role"
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                placeholder="e.g., Software Engineer, Product Manager, Data Scientist..."
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Posting URL (Optional)"
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                placeholder="Paste the job posting URL here..."
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Provide a job posting URL to generate experiences tailored to the specific job requirements"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleGenerate}
                disabled={loading || !jobRole.trim()}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Generating Experiences...' : 'Generate Experiences'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          icon={<ErrorIcon />}
        >
          {error}
        </Alert>
      )}

      {experiences.length > 0 && (
        <Card elevation={0} sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
              <AutoFixHighIcon color="primary" />
              <Typography variant="h5" fontWeight="600">
                Generated Experiences
              </Typography>
            </Stack>

            <Stack spacing={2}>
              {experiences.map((experience, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    position: 'relative'
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2, pr: 4 }}>
                    {experience}
                  </Typography>
                  <Box sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1
                  }}>
                    <Tooltip title={copiedIndex === index ? "Copied!" : "Copy to clipboard"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(experience, index)}
                        color={copiedIndex === index ? "success" : "default"}
                      >
                        {copiedIndex === index ? <CheckCircleIcon /> : <ContentCopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GenerateExperience; 